import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles, categories, faqs } from '@/mocks/articles';
import { useState } from 'react';

export default function Home() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'loading' | 'success'>('idle');

  const hotArticles = articles.filter((a) => a.hot).slice(0, 6);
  const featuredArticles = articles.filter((a) => a.featured).slice(0, 3);

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubscribeStatus('loading');
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setSubscribeStatus('success');
      return;
    }
    try {
      const res = await fetch('https://readdy.ai/api/form/d9s94a7nahb7382ovsdg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: formData.get('email') as string }).toString(),
      });
      const text = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setSubscribeStatus('success');
        setEmail('');
      } else {
        setSubscribeStatus('idle');
      }
    } catch {
      setSubscribeStatus('idle');
    }
  };

  return (
    <>
      <Navbar />

      <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Elegant%20fashion%20lifestyle%20scene%20with%20beautiful%20neutral%20toned%20clothing%20hanging%20on%20minimalist%20wooden%20rack%2C%20soft%20warm%20natural%20light%20streaming%20from%20large%20window%2C%20clean%20Scandinavian%20interior%20with%20cream%20walls%20and%20light%20oak%20floor%2C%20feminine%20aesthetic%2C%20serene%20atmosphere%2C%20fashion%20editorial%20photography&width=1600&height=900&seq=hero-home-2026&orientation=landscape"
          alt="穿搭换装指南"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="relative z-10 w-full text-center px-4 md:px-6">
          <h1 className="text-3xl md:text-5xl font-heading text-background-50 mb-4 leading-tight">
            {t('hero.title')}
          </h1>
          <p className="text-sm md:text-lg text-background-50/90 max-w-2xl mx-auto mb-8 leading-relaxed">
            {t('hero.subtitle')}
          </p>
          <Link
            to="/category/daily"
            className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            {t('hero.cta')}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">探索穿搭分类</h2>
          <p className="text-sm text-foreground-500">找到最适合你的穿搭技巧与换装指南</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="group bg-background-50 border border-background-200/70 rounded-lg p-4 md:p-5 text-center transition-all duration-300 hover:border-primary-200 cursor-pointer"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                <i className={`${cat.icon} text-xl md:text-2xl`}></i>
              </div>
              <h3 className="text-sm md:text-base font-heading text-foreground-800 mb-1 whitespace-nowrap">{cat.name}</h3>
              <p className="text-xs text-foreground-500 leading-relaxed hidden md:block">{cat.description}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-background-100/80 py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">{t('section.hot')}</h2>
              <p className="text-sm text-foreground-500">大家最爱看的穿搭技巧</p>
            </div>
            <Link
              to="/category/daily"
              className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {hotArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 transition-all duration-300 hover:border-primary-200 cursor-pointer"
              >
                <div className="relative w-full h-48 md:h-52 overflow-hidden">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <span className="absolute top-3 left-3 bg-primary-500 text-background-50 text-xs px-2 py-1 rounded-md">
                    {article.category}
                  </span>
                </div>
                <div className="p-4 md:p-5">
                  <h3 className="text-base md:text-lg font-heading text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-500 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-500 line-clamp-2 mb-3 leading-relaxed">{article.excerpt}</p>
                  <div className="flex items-center gap-3 text-xs text-foreground-400">
                    <span className="flex items-center gap-1"><i className="ri-time-line"></i> {article.readTime}</span>
                    <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {article.publishDate}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-8 text-center sm:hidden">
            <Link
              to="/category/daily"
              className="inline-flex items-center gap-1 text-sm text-primary-500 font-medium cursor-pointer whitespace-nowrap"
            >
              查看全部文章 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">{t('section.featured')}</h2>
          <p className="text-sm text-foreground-500">编辑精选的优质穿搭内容</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {featuredArticles.map((article, idx) => (
            <Link
              key={article.id}
              to={`/article/${article.slug}`}
              className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 transition-all duration-300 hover:border-accent-200 cursor-pointer flex flex-col"
            >
              <div className="relative w-full h-56 overflow-hidden">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                {idx === 0 && (
                  <span className="absolute top-3 right-3 bg-accent-500 text-background-50 text-xs px-2 py-1 rounded-md font-medium">
                    编辑推荐
                  </span>
                )}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <span className="text-xs text-accent-600 font-medium mb-2">{article.category}</span>
                <h3 className="text-lg font-heading text-foreground-900 mb-2 leading-snug group-hover:text-accent-500 transition-colors">
                  {article.title}
                </h3>
                <p className="text-sm text-foreground-500 line-clamp-2 mb-4 leading-relaxed flex-1">{article.excerpt}</p>
                <div className="flex items-center gap-3 text-xs text-foreground-400">
                  <span>{article.author}</span>
                  <span>·</span>
                  <span>{article.readTime}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-background-100/80 py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">{t('section.faq')}</h2>
            <p className="text-sm text-foreground-500">关于穿搭换装，你可能想问的</p>
          </div>
          <div className="flex flex-col gap-3">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                >
                  <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">{faq.question}</span>
                  <i
                    className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-300 flex-shrink-0 ${
                      openFaq === idx ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                <div
                  className={`transition-all duration-300 overflow-hidden ${
                    openFaq === idx ? 'max-h-96 pb-5 px-5' : 'max-h-0'
                  }`}
                >
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-6 py-14 md:py-20">
        <div className="bg-accent-100/60 rounded-xl p-8 md:p-12 text-center">
          <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">{t('section.newsletter')}</h2>
          <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">{t('section.newsletter.desc')}</p>
          {subscribeStatus === 'success' ? (
            <p className="text-primary-500 font-medium text-sm">{t('newsletter.success')}</p>
          ) : (
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto">
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-honeypot"
              />
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('newsletter.placeholder')}
                required
                className="w-full sm:flex-1 px-4 py-2.5 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
              />
              <button
                type="submit"
                disabled={subscribeStatus === 'loading'}
                className="w-full sm:w-auto bg-primary-500 text-background-50 px-6 py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {subscribeStatus === 'loading' ? '提交中...' : t('newsletter.subscribe')}
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
      <ScrollToTop />
    </>
  );
}